import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, doc as fsDoc, getDoc, onSnapshot, query as fsQuery, where } from 'firebase/firestore';
import { db } from '../../../../firebase';
import { Customer } from '../../customers/CustomerInterfaces';
import { useAppData } from '../../../providers/AppDataProvider';
import { Job } from '../../job-summary/Interfaces';
import { Invoice, Bill, INVOICE_STATUS_CONFIG, BILL_STATUS_CONFIG } from '../../../types/accounting';
import NewCustomerModal from '../../customers/NewCustomerModal';
import JobEditModal from '../../job-summary/JobEditModal';
import JobWorkspaceModal from '../../job-summary/JobWorkspaceModal';
import InvoiceViewModal from '../../invoices/components/InvoiceViewModal';
import { InvoiceProvider } from '../../invoices/contexts/InvoiceContext';
import BillViewModal from '../../bills/components/BillViewModal';
import { BillsProvider } from '../../bills/contexts/BillsContext';
import { isDateOverdue } from '../../../utils/dateUtils';
import { CONTACT_TYPE_BADGE_CLASS } from '../../../constants/contactTypes';
import { ClientConversationsTab } from './ClientConversationsTab';
import { ClientAdsTab } from './ClientAdsTab';
import { ClientHostingTab } from './ClientHostingTab';
import { useAndreiChat } from '../../../contexts/AndreiChatContext';
import { useSubscriptions, termInfo, endInfo } from '../../../contexts/SubscriptionsContext';
import { useEmailPanel } from '../../mail/contexts/EmailPanelContext';
import { useCustomerCorrespondence } from '../../../hooks/useCustomerCorrespondence';

/** Tolerant date parser (Firestore Timestamp / Date / ISO string / epoch). */
const toDate = (value: any): Date | null => {
  if (value === null || value === undefined || value === '') return null;
  let date: Date;
  if (typeof value === 'object' && value && 'seconds' in value) date = new Date((value as any).seconds * 1000);
  else if (value instanceof Date) date = value;
  else if (typeof value === 'string') date = new Date(value);
  else if (typeof value === 'number') date = new Date(value < 1e12 ? value * 1000 : value);
  else return null;
  return isNaN(date.getTime()) ? null : date;
};
const relTime = (value: any): string => {
  const d = toDate(value);
  if (!d) return 'never';
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days}d ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
};

// Timeline: initial curated count, then "Show more" reveals pages of this size.
const TIMELINE_CAP = 18;
const TIMELINE_PAGE = 50;

interface ClientRevenueData {
  total: number;
  outstanding: number;
  paid: number;
}

interface ClientJobCounts {
  active: number;
  completed: number;
  total: number;
}

interface ClientDetailPanelProps {
  client: Customer;
  onClose: () => void;
  getClientRevenue: (customerId: string) => ClientRevenueData;
  getClientJobCounts: (customerId: string) => ClientJobCounts;
}

type TabType = 'overview' | 'jobs' | 'invoices' | 'recurring' | 'conversations' | 'ads' | 'hosting';

export function ClientDetailPanel({
  client,
  onClose,
  getClientRevenue,
  getClientJobCounts,
}: ClientDetailPanelProps) {
  const { jobs, invoices, bills, events, customers } = useAppData();
  const { openEmailPanel } = useEmailPanel();
  const { emails: timelineEmails, error: correspondenceError } = useCustomerCorrespondence(client.id || '', client);
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [tlSearch, setTlSearch] = useState('');
  const [tlVisible, setTlVisible] = useState(TIMELINE_CAP);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [showJobEditModal, setShowJobEditModal] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [selectedBill, setSelectedBill] = useState<Bill | null>(null);
  const navigate = useNavigate();

  /** Open an auto-generated invoice straight from a subscription payment row. */
  const openInvoiceById = async (invoiceId: string) => {
    try {
      const snap = await getDoc(fsDoc(db, 'invoices', invoiceId));
      if (snap.exists()) setSelectedInvoice({ id: snap.id, ...(snap.data() as any) } as Invoice);
    } catch (err) {
      console.warn('[ClientDetailPanel] open invoice failed:', err);
    }
  };

  // Suppliers are payees — their money surface is BILLS (ACCPAY), not sales
  // invoices. When true, the "Invoices" tab/stat becomes "Bills".
  const isSupplier = client.contactType === 'Supplier';

  const revenue = getClientRevenue(client.id || '');
  const jobCounts = getClientJobCounts(client.id || '');

  const clientJobs = useMemo(() => {
    return jobs.filter(job => job.customerId.includes(client.id || ''));
  }, [jobs, client.id]);

  const clientInvoices = useMemo(() => {
    return invoices
      .filter(inv => inv.Contact.ContactID === client.id)
      .map(inv => ({
        id: inv.InvoiceID,
        invoiceNumber: inv.InvoiceNumber,
        reference: inv.Reference || null,
        status: inv.Status as Invoice['status'],
        currencyCode: inv.CurrencyCode || 'AUD',
        total: inv.Total || 0,
        amountDue: inv.AmountDue ?? 0,
        amountPaid: inv.AmountPaid ?? 0,
        contactId: inv.Contact?.ContactID || null,
        contactName: inv.Contact?.Name || null,
        sentToContact: inv.SentToContact || false,
        date: inv.DateString || '',
        dueDate: inv.DueDateString || '',
        createdAt: null,
        updatedAt: null,
      } as Invoice));
  }, [invoices, client.id]);

  // Supplier bills (ACCPAY) for this contact — mirrors clientInvoices but off the
  // native `bills` collection. Mapped to the Bill shape BillViewModal consumes.
  //
  // Matching is tolerant because many historical/auto-ingested bills were saved
  // with a free-text supplier NAME but a BLANK contactId (e.g. bills labelled
  // "SiteGround" vs the record "Siteground Hosting & Domains"). We therefore
  // match by:
  //   1. exact contactId  (the durable link — always trusted), OR
  //   2. name — BUT ONLY for orphan bills. A bill already linked (by a contactId
  //      that resolves to an EXISTING customer) is never re-attributed by name,
  //      so this can never steal a properly-linked bill onto the wrong contact.
  //      A dangling contactId (points to a deleted/re-created contact — resolves
  //      to nobody) is treated as an orphan and recovered by name, mirroring
  //      repair-orphaned-contacts (`!cId || !customerIds.has(cId)`); otherwise
  //      such a bill would be invisible on every contact.
  const clientBills = useMemo(() => {
    const norm = (s: any) => String(s || '').trim().toLowerCase();
    const names = [norm(client.company), norm(client.name)].filter((n) => n.length > 0);
    const customerIds = new Set(customers.map((c) => c.id));
    const matchesName = (billName: any): boolean => {
      const bn = norm(billName);
      if (!bn) return false;
      if (names.includes(bn)) return true; // exact
      // Confident containment (>=5 chars either way) — "siteground" ⊂ "siteground hosting & domains".
      return bn.length >= 5 && names.some((n) => n.length >= 5 && (n.includes(bn) || bn.includes(n)));
    };
    return bills
      .filter((b) => {
        const cid = b.Contact?.ContactID || '';
        if (cid && customerIds.has(cid)) return cid === client.id; // resolvable link → strict id
        return matchesName(b.Contact?.Name);                        // orphan / dangling id → name match
      })
      .map(b => ({
        id: b.InvoiceID,
        billNumber: b.InvoiceNumber,
        reference: b.Reference || null,
        status: b.Status as Bill['status'],
        currencyCode: b.CurrencyCode || 'AUD',
        total: b.Total || 0,
        amountDue: b.AmountDue ?? 0,
        amountPaid: b.AmountPaid ?? 0,
        contactId: b.Contact?.ContactID || null,
        contactName: b.Contact?.Name || null,
        date: b.DateString || '',
        dueDate: b.DueDateString || '',
        createdAt: null,
        updatedAt: null,
      } as Bill));
  }, [bills, client.id, client.company, client.name, customers]);

  // Get client's events
  const clientEvents = useMemo(() => {
    return events.filter(event => event.client?.includes(client.id || ''));
  }, [events, client.id]);

  // Live recurring-invoice templates for this contact (native repeating_invoices
  // collection — keyed by contactId, same id this client is matched on).
  const [recurringTemplates, setRecurringTemplates] = useState<any[]>([]);
  useEffect(() => {
    // Clear FIRST — otherwise switching contact shows the previous contact's
    // templates until the new snapshot lands.
    setRecurringTemplates([]);
    if (!client.id) return;
    const unsub = onSnapshot(
      fsQuery(collection(db, 'repeating_invoices'), where('contactId', '==', client.id)),
      (snap) => setRecurringTemplates(snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }))),
      (err) => { console.warn('[ClientDetailPanel] recurring sub error:', err); setRecurringTemplates([]); },
    );
    return () => unsub();
  }, [client.id]);

  // Recurring Stripe arrangements linked to this contact (negotiated monthly
  // support deals). Read from the shared live listener — each paid cycle has
  // already generated a PAID invoice, which shows on the Invoices tab.
  const { subscriptions: allSubscriptions, paymentsFor } = useSubscriptions();
  const clientSubscriptions = useMemo(
    () => allSubscriptions.filter((s) => s.linkStatus === 'linked' && s.linkedCustomerId === client.id),
    [allSubscriptions, client.id],
  );
  const activeSubscriptions = useMemo(
    () => clientSubscriptions.filter((s) => s.status === 'active' || s.status === 'trialing'),
    [clientSubscriptions],
  );
  // A declined card on this contact — worth a mark on the tab itself.
  const failingSubscriptions = useMemo(
    () => clientSubscriptions.filter((s) => s.dunning?.failing === true && !s.testMode),
    [clientSubscriptions],
  );

  // Live Google Ads change sets for this contact's linked Ads account, so
  // proposed / approved / applied changes show on the Relationship Timeline.
  // Read-only join on the existing customers.googleAdsCustomerId ↔
  // googleAdsChangeSets.accountId link — no new access path.
  const [gadsChanges, setGadsChanges] = useState<any[]>([]);
  useEffect(() => {
    const cid = client.googleAdsCustomerId;
    if (!cid) { setGadsChanges([]); return; }
    const unsub = onSnapshot(
      fsQuery(collection(db, 'googleAdsChangeSets'), where('accountId', '==', cid)),
      (snap) => setGadsChanges(snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }))),
      (err) => { console.warn('[ClientDetailPanel] gads change sub error:', err); setGadsChanges([]); },
    );
    return () => unsub();
  }, [client.googleAdsCustomerId]);

  // Collapse the timeline + clear search when switching to a different customer.
  useEffect(() => { setTlVisible(TIMELINE_CAP); setTlSearch(''); }, [client.id]);

  const activeRecurring = useMemo(
    () => recurringTemplates.filter((r) => String(r.status || '').toUpperCase() === 'ACTIVE'),
    [recurringTemplates],
  );
  // Active recurring templates that generate BILLS (vs sales invoices) — the
  // recurring banner/indicator on a supplier's Bills tab.
  const activeRecurringBills = useMemo(
    () => activeRecurring.filter((r) => String(r.templateType || '') === 'bill'),
    [activeRecurring],
  );
  /** Money IN templates only — the opposite direction to a bill template. */
  const activeRecurringInvoices = useMemo(
    () => activeRecurring.filter((r) => String(r.templateType || '') !== 'bill'),
    [activeRecurring],
  );
  // Both mechanisms billing the same customer on the same cadence is very likely
  // double-billing — surface it rather than quietly showing two figures.
  const doubleBillingRisk = useMemo(() => {
    // Compare CYCLE LENGTH IN DAYS, not the bare unit — a 3-month Stripe cycle
    // and a MONTHLY template are not the same cadence. Bill templates are money
    // OUT, so they can never double-bill against a receivable.
    const invoiceTemplates = activeRecurring.filter((r) => String(r.templateType || '') !== 'bill');
    if (activeSubscriptions.length === 0 || invoiceTemplates.length === 0) return false;
    const days = (unit: string, count: number) => {
      switch (unit) {
        case 'day': return count;
        case 'week': return 7 * count;
        case 'year': return 365 * count;
        default: return 30 * count;
      }
    };
    const subDays = new Set(activeSubscriptions.map(
      (s) => days(String(s.interval || 'month').toLowerCase(), Number(s.intervalCount) || 1),
    ));
    return invoiceTemplates.some((r) => {
      const f = String(r.frequency || '').toUpperCase();
      const d = f === 'WEEKLY' ? 7 : f === 'QUARTERLY' ? 90 : f === 'YEARLY' ? 365 : 30;
      return subDays.has(d);
    });
  }, [activeSubscriptions, activeRecurring]);

  // The Recurring tab button disappears when neither mechanism exists; without
  // this the tab CONTENT would stay rendered with no way back to it.
  useEffect(() => {
    if (activeTab === 'recurring' && clientSubscriptions.length === 0 && activeRecurring.length === 0) {
      setActiveTab('overview');
    }
  }, [activeTab, clientSubscriptions.length, activeRecurring.length]);
  // Best-effort template total, mirroring the server's per-line cents GST math.
  const templateTotal = (r: any): number => {
    const items = Array.isArray(r.lineItems) ? r.lineItems : [];
    const amt = r.lineAmountTypes || 'Exclusive';
    let subC = 0; let gstC = 0;
    for (const li of items) {
      const rawC = Math.round((Number(li.quantity) || 0) * (Number(li.unitAmount) || 0) * 100);
      const noTax = amt === 'NoTax' || li.taxType === 'GST_FREE' || li.taxType === 'NONE';
      const taxC = noTax ? 0 : (amt === 'Inclusive' ? Math.round(rawC / 11) : Math.round(rawC * 0.1));
      subC += amt === 'Inclusive' ? (rawC - taxC) : rawC;
      gstC += taxC;
    }
    return (subC + gstC) / 100;
  };
  const freqLabel = (f: any): string => {
    const s = String(f || '').toUpperCase();
    return s ? s.charAt(0) + s.slice(1).toLowerCase() : '—';
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (value: any) => {
    if (!value) return '-';
    
    let date: Date;
    
    // Handle Firestore Timestamp objects (have seconds and nanoseconds)
    if (value && typeof value === 'object' && 'seconds' in value) {
      date = new Date(value.seconds * 1000);
    }
    // Handle Date objects
    else if (value instanceof Date) {
      date = value;
    }
    else if (typeof value === 'string') {
      date = new Date(value);
    }
    // Handle numeric timestamps
    else if (typeof value === 'number') {
      // If it looks like seconds (< 1e12), convert to ms
      const ms = value < 1e12 ? value * 1000 : value;
      date = new Date(ms);
    }
    else {
      return '-';
    }
    
    // Check if date is valid
    if (isNaN(date.getTime())) return '-';
    
    return date.toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  // ── Relationship intelligence (deterministic — Andrei stays an explicit
  //    affordance, not an auto-call on every page view). ──────────────────────
  const { openAndreiChat } = useAndreiChat();
  const askAndrei = (prefill: string) =>
    openAndreiChat({ prefill, context: { type: 'customer', id: client.id || '', label: client.company || client.name } });

  const paidCount = clientInvoices.filter(i => i.status === 'PAID').length;
  const totalInvoices = clientInvoices.length;
  const payPct = totalInvoices > 0 ? Math.round((paidCount / totalInvoices) * 100) : null;
  const overdueInvoices = clientInvoices.filter(
    i => i.amountDue > 0 && i.status === 'APPROVED' && isDateOverdue(i.dueDate),
  );
  const isFormer = /former|lapsed|lost/i.test(client.contactType || '');
  const isLead = /lead|prospect/i.test(client.contactType || '');

  // Most recent touch across every signal (contact field + events + invoices + jobs).
  let lastTouchMs = 0;
  const touch = (v: any) => { const d = toDate(v); if (d) lastTouchMs = Math.max(lastTouchMs, d.getTime()); };
  touch(client.lastContacted);
  clientEvents.forEach(e => touch(e.start));
  clientInvoices.forEach(i => touch(i.date));
  clientJobs.forEach(j => touch(j.creationDate));

  // A derived signal tag — what the relationship IS right now (not a pile of numbers).
  const signal: { tone: string; label: string } =
    overdueInvoices.length > 0 ? { tone: 'danger', label: 'Needs chasing' }
    : revenue.outstanding > 0 ? { tone: 'warning', label: 'Outstanding balance' }
    : jobCounts.active > 0 ? { tone: 'success', label: 'Active engagement' }
    : (isFormer && revenue.total > 0 && (payPct ?? 0) >= 80) ? { tone: 'primary', label: 'Win-back candidate' }
    : (activeRecurring.length > 0 || activeSubscriptions.length > 0) ? { tone: 'success', label: 'Recurring client' }
    : isLead ? { tone: 'info', label: 'New lead' }
    : { tone: 'secondary', label: 'Quiet' };

  const stateBits = [
    client.contactType || null,
    payPct !== null ? `${payPct}% paid (${paidCount}/${totalInvoices})` : null,
    // Say the MECHANISM, not just "recurring" twice — these are opposite things.
    activeRecurring.length > 0
      ? `${activeRecurring.length} scheduled invoice${activeRecurring.length === 1 ? '' : 's'}` : null,
    activeSubscriptions.length > 0
      ? `${formatCurrency(activeSubscriptions.reduce((s, x) => s + (x.currentAmount || 0), 0))}/mo auto-charged`
      : null,
    lastTouchMs ? `last touch ${relTime(lastTouchMs)}` : null,
    jobCounts.total > 0 ? `${jobCounts.total} job${jobCounts.total === 1 ? '' : 's'}` : null,
    revenue.total > 0 ? `${formatCurrency(revenue.total)} lifetime` : null,
  ].filter(Boolean).join('  ·  ');

  // The single most important move on this relationship right now.
  const nextAction: { tone: string; label: string; cta: string | null; run: (() => void) | null } = (() => {
    if (overdueInvoices.length > 0) {
      const sum = overdueInvoices.reduce((s, i) => s + (i.amountDue || 0), 0);
      return { tone: 'danger', label: `Chase ${overdueInvoices.length} overdue invoice${overdueInvoices.length === 1 ? '' : 's'} (${formatCurrency(sum)})`, cta: 'View invoices', run: () => setActiveTab('invoices') };
    }
    const stalled = clientJobs.find(j => {
      if (!['Open', 'Active', 'Pending', 'Awaiting Client Response', 'Overdue'].includes(j.status)) return false;
      const d = toDate(j.dueDate); return !!d && d.getTime() < Date.now();
    });
    if (stalled) return { tone: 'warning', label: `Job overdue: ${stalled.jobLabel}`, cta: 'Open job', run: () => { setSelectedJob(stalled); setShowTaskModal(true); } };
    if (jobCounts.active > 0) return { tone: 'success', label: `${jobCounts.active} active job${jobCounts.active === 1 ? '' : 's'} in progress`, cta: 'View jobs', run: () => setActiveTab('jobs') };
    if (isFormer && revenue.total > 0 && (payPct ?? 0) >= 80) return { tone: 'primary', label: `Former client, ${payPct}% payer — strong win-back candidate`, cta: 'Draft win-back', run: () => askAndrei(`Draft a warm win-back email to re-engage ${client.company || client.name}, a former client with a strong payment history.`) };
    if (isLead) return { tone: 'info', label: 'New lead — no jobs yet', cta: 'Ask Andrei', run: () => askAndrei(`How should I approach converting ${client.company || client.name} from a lead into a paying client?`) };
    return { tone: 'secondary', label: 'Relationship healthy — nothing needs you right now.', cta: null, run: null };
  })();

  // Unified timeline — the relationship's story (jobs + invoices + events) in one
  // reverse-chron stream. Capped so a recurring-billing client doesn't bury it.
  const timeline = (() => {
    const items: { ms: number; icon: string; tone: string; title: string; sub: string; run?: () => void }[] = [];
    for (const j of clientJobs) {
      const d = toDate(j.creationDate); if (!d) continue;
      items.push({ ms: d.getTime(), icon: 'briefcase', tone: 'primary', title: `Job: ${j.jobLabel}`, sub: j.status, run: () => { setSelectedJob(j); setShowTaskModal(true); } });
    }
    for (const inv of clientInvoices) {
      const d = toDate(inv.date); if (!d) continue;
      const paid = inv.status === 'PAID';
      const overdue = inv.amountDue > 0 && inv.status === 'APPROVED' && isDateOverdue(inv.dueDate);
      items.push({ ms: d.getTime(), icon: paid ? 'check-circle' : 'receipt', tone: paid ? 'success' : overdue ? 'danger' : 'info', title: `Invoice ${inv.invoiceNumber} · ${formatCurrency(inv.total)}`, sub: paid ? 'Paid' : (INVOICE_STATUS_CONFIG[inv.status]?.label || inv.status), run: () => setSelectedInvoice(inv) });
    }
    for (const bill of clientBills) {
      const d = toDate(bill.date); if (!d) continue;
      const paid = bill.status === 'PAID';
      const overdue = bill.amountDue > 0 && (bill.status === 'APPROVED' || bill.status === 'SENT') && isDateOverdue(bill.dueDate);
      items.push({ ms: d.getTime(), icon: paid ? 'check-circle' : 'cash-stack', tone: paid ? 'success' : overdue ? 'danger' : 'warning', title: `Bill ${bill.billNumber} · ${formatCurrency(bill.total)}`, sub: paid ? 'Paid' : (BILL_STATUS_CONFIG[bill.status]?.label || bill.status), run: () => setSelectedBill(bill) });
    }
    for (const e of clientEvents) {
      const d = toDate(e.start); if (!d) continue;
      items.push({ ms: d.getTime(), icon: 'calendar-event', tone: 'secondary', title: e.title, sub: e.description || '' });
    }
    for (const em of timelineEmails) {
      const d = toDate(em.createdAt); if (!d || d.getTime() <= 0) continue;
      const out = em.emailType === 'sent';
      const who = em.from?.[0]?.name || em.from?.[0]?.address || em.sender || 'unknown';
      const toWho = (em.to || [])[0] || '';
      items.push({
        ms: d.getTime(),
        icon: out ? 'envelope-paper' : 'envelope',
        tone: out ? 'info' : 'primary',
        title: `${out ? 'Sent' : 'Email'}: ${em.subject || '(no subject)'}`,
        sub: out ? `to ${toWho}` : `from ${who}`,
        run: () => openEmailPanel('viewEmail', em),
      });
    }
    // A paid Stripe cycle would otherwise emit TWO rows for the same money — the
    // generated invoice (pushed above) and the payment. Only show the payment
    // when its invoice isn't already on the timeline.
    const invoiceNumbersOnTimeline = new Set(clientInvoices.map((i) => i.invoiceNumber));
    for (const sub of clientSubscriptions) {
      for (const p of paymentsFor(sub.id)) {
        if (p.nativeInvoiceNumber && invoiceNumbersOnTimeline.has(p.nativeInvoiceNumber)) continue;
        const d = toDate(p.paidAt); if (!d) continue;
        items.push({
          ms: d.getTime(),
          icon: 'credit-card',
          tone: 'success',
          title: `Auto-charged · ${formatCurrency(p.amount)}`,
          sub: [sub.planName || 'Monthly support', p.nativeInvoiceNumber ? `invoiced ${p.nativeInvoiceNumber}` : 'not yet invoiced']
            .filter(Boolean).join(' · '),
          run: p.nativeInvoiceId ? () => openInvoiceById(p.nativeInvoiceId!) : undefined,
        });
      }
    }
    for (const cs of gadsChanges) {
      const d = toDate(cs.createdAt); if (!d) continue;
      const st = String(cs.status || '');
      const tone = st === 'applied' ? 'success'
        : st === 'pending_review' ? 'warning'
        : (st === 'failed' || st === 'reverted') ? 'danger' : 'secondary';
      const label = st === 'pending_review' ? 'Proposed'
        : st === 'applied' ? 'Approved & applied'
        : st === 'rejected' ? 'Rejected'
        : st === 'reverted' ? 'Reverted'
        : st === 'applying' ? 'Applying' : st.replace(/_/g, ' ');
      const by = cs.source ? ` · ${cs.source}` : '';
      items.push({
        ms: d.getTime(),
        icon: 'arrow-repeat',
        tone,
        title: `Google Ads: ${cs.summary || 'change'}`,
        sub: `${label}${by}`,
      });
    }
    return items.sort((a, b) => b.ms - a.ms);
  })();

  // Badge variant resolution is centralised in constants/contactTypes.ts
  // so all surfaces (detail panel, list view, future filters) use the
  // same palette. Adding a new contact type only requires editing the
  // constants file.
  const getContactTypeBadge = (type: string | undefined) => {
    const cls = type && (CONTACT_TYPE_BADGE_CLASS as Record<string, string>)[type];
    if (!cls) return <span className="badge badge-light-secondary fs-7">Unknown</span>;
    return <span className={`badge ${cls} fs-7`}>{type}</span>;
  };

  const getJobStatusBadge = (status: string) => {
    const statusColors: Record<string, string> = {
      'Open': 'badge-light-primary',
      'Active': 'badge-light-success',
      'Pending': 'badge-light-warning',
      'Awaiting Client Response': 'badge-light-info',
      'Overdue': 'badge-light-danger',
      'Closed': 'badge-light-secondary',
      'Cancelled': 'badge-light-dark',
      'Archived': 'badge-light-dark',
    };
    return <span className={`badge ${statusColors[status] || 'badge-light-secondary'}`}>{status}</span>;
  };

  const renderOverviewTab = () => (
    <div className="row g-5">
      {renderTimeline()}

      {/* Contact Information */}
      <div className="col-lg-6">
        <div className="card h-100">
          <div className="card-header">
            <h3 className="card-title fw-bold">Contact Information</h3>
          </div>
          <div className="card-body">
            <div className="d-flex flex-column gap-4">
              {/* Primary Contact */}
              <div>
                <div className="text-muted fs-7 mb-1">Primary Contact</div>
                <div className="fw-bold fs-5">{client.name || '-'}</div>
              </div>

              {/* Email */}
              {client.email && (
                <div>
                  <div className="text-muted fs-7 mb-1">Email</div>
                  <a href={`mailto:${client.email}`} className="text-primary fw-semibold">
                    <i className="bi bi-envelope me-2"></i>
                    {client.email}
                  </a>
                </div>
              )}

              {/* Phone */}
              {client.phone && (
                <div>
                  <div className="text-muted fs-7 mb-1">Phone</div>
                  <a href={`tel:${client.phone}`} className="text-gray-800">
                    <i className="bi bi-telephone me-2"></i>
                    {client.phone}
                  </a>
                </div>
              )}

              {/* Address */}
              {client.address && (
                <div>
                  <div className="text-muted fs-7 mb-1">Address</div>
                  <div className="text-gray-800">
                    <i className="bi bi-geo-alt me-2"></i>
                    {client.address}
                  </div>
                </div>
              )}

              {/* Industry */}
              {client.industry && (
                <div>
                  <div className="text-muted fs-7 mb-1">Industry</div>
                  <div className="text-gray-800">{client.industry}</div>
                </div>
              )}

              {/* Secondary Contacts */}
              {client.contacts && client.contacts.length > 0 && (
                <div>
                  <div className="text-muted fs-7 mb-2">Additional Contacts</div>
                  <div className="d-flex flex-column gap-2">
                    {client.contacts.map((contact, idx) => (
                      <div key={idx} className="bg-light rounded p-3">
                        <div className="fw-semibold">{contact.name}</div>
                        {contact.email && (
                          <div className="text-muted fs-8">
                            <i className="bi bi-envelope me-1"></i>
                            {contact.email}
                          </div>
                        )}
                        {contact.phone && (
                          <div className="text-muted fs-8">
                            <i className="bi bi-telephone me-1"></i>
                            {contact.phone}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Financial Summary */}
      <div className="col-lg-6">
        <div className="card h-100">
          <div className="card-header">
            <h3 className="card-title fw-bold">Financial Summary</h3>
          </div>
          <div className="card-body">
            <div className="d-flex flex-column gap-4">
              {/* Lifetime Revenue */}
              <div className="d-flex justify-content-between align-items-center p-4 bg-light-success rounded">
                <div>
                  <div className="text-muted fs-7">Lifetime Revenue</div>
                  <div className="fs-2 fw-bold text-success">{formatCurrency(revenue.total)}</div>
                </div>
                <i className="bi bi-cash-stack fs-1 text-success opacity-50"></i>
              </div>

              {/* Outstanding */}
              <div className="d-flex justify-content-between align-items-center p-4 bg-light-warning rounded">
                <div>
                  <div className="text-muted fs-7">Outstanding</div>
                  <div className="fs-2 fw-bold text-warning">{formatCurrency(revenue.outstanding)}</div>
                </div>
                <i className="bi bi-clock fs-1 text-warning opacity-50"></i>
              </div>

              {/* Invoice Stats */}
              <div className="row g-3">
                <div className="col-6">
                  <div className="bg-light rounded p-3 text-center">
                    <div className="fs-3 fw-bold text-gray-800">{clientInvoices.length}</div>
                    <div className="text-muted fs-8">Total Invoices</div>
                  </div>
                </div>
                <div className="col-6">
                  <div className="bg-light rounded p-3 text-center">
                    <div className="fs-3 fw-bold text-gray-800">
                      {clientInvoices.filter(i => i.status === 'PAID').length}
                    </div>
                    <div className="text-muted fs-8">Paid Invoices</div>
                  </div>
                </div>
              </div>

              {/* Accounting link */}
              <div className="mt-3 p-3 bg-light rounded">
                <div className="d-flex align-items-center">
                  <i className="bi bi-receipt text-primary fs-4 me-2"></i>
                  <div>
                    <div className="fs-7 fw-semibold text-gray-800">Native Accounting</div>
                    <div className="fs-8 text-muted">{clientInvoices.length} invoice{clientInvoices.length !== 1 ? 's' : ''} on record</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Notes */}
      {client.notes && (
        <div className="col-12">
          <div className="card">
            <div className="card-header">
              <h3 className="card-title fw-bold">Notes</h3>
            </div>
            <div className="card-body">
              <p className="text-gray-800 mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                {client.notes}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Metadata */}
      <div className="col-12">
        <div className="card">
          <div className="card-body py-3">
            <div className="d-flex flex-wrap gap-5">
              <div>
                <span className="text-muted fs-8">Created:</span>
                <span className="ms-2 text-gray-800 fs-7">{formatDate(client.createdAt)}</span>
              </div>
              <div>
                <span className="text-muted fs-8">Last Contacted:</span>
                <span className="ms-2 text-gray-800 fs-7">{formatDate(client.lastContacted)}</span>
              </div>
              {client.referralSource && (
                <div>
                  <span className="text-muted fs-8">Referral Source:</span>
                  <span className="ms-2 text-gray-800 fs-7">{client.referralSource}</span>
                </div>
              )}
              {client.googleReviewStars && (
                <div>
                  <span className="text-muted fs-8">Google Review:</span>
                  <span className="ms-2 text-gray-800 fs-7">{client.googleReviewStars}/5 ⭐</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderJobsTab = () => (
    <div className="card">
      <div className="card-header">
        <h3 className="card-title fw-bold">Jobs ({clientJobs.length})</h3>
      </div>
      <div className="card-body py-3">
        {clientJobs.length === 0 ? (
          <div className="text-center py-10">
            <i className="bi bi-briefcase fs-1 text-muted"></i>
            <div className="text-muted mt-3">No jobs found for this client</div>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4">
              <thead>
                <tr className="fw-bold text-muted">
                  <th className="min-w-200px">Job</th>
                  <th className="min-w-100px">Status</th>
                  <th className="min-w-100px">Created</th>
                  <th className="min-w-100px">Due Date</th>
                  <th className="min-w-50px text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {clientJobs.map((job) => (
                  <tr key={job.id}>
                    <td>
                      <div className="d-flex flex-column">
                        <span className="text-gray-800 fw-bold">{job.jobLabel}</span>
                        {job.description && (
                          <span className="text-muted fs-8 text-truncate" style={{ maxWidth: 300 }}>
                            {job.description}
                          </span>
                        )}
                      </div>
                    </td>
                    <td>{getJobStatusBadge(job.status)}</td>
                    <td className="text-muted fs-7">{formatDate(job.creationDate)}</td>
                    <td className="text-muted fs-7">{formatDate(job.dueDate)}</td>
                    <td className="text-end">
                      <button
                        className="btn btn-sm btn-icon btn-light-primary me-1"
                        onClick={() => {
                          setSelectedJob(job);
                          setShowTaskModal(true);
                        }}
                        title="View Tasks"
                      >
                        <i className="bi bi-list-task"></i>
                      </button>
                      <button
                        className="btn btn-sm btn-icon btn-light-info"
                        onClick={() => {
                          setSelectedJob(job);
                          setShowJobEditModal(true);
                        }}
                        title="Edit Job"
                      >
                        <i className="bi bi-pencil"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

  const renderInvoicesTab = () => (
    <div className="card">
      <div className="card-header d-flex align-items-center justify-content-between">
        <h3 className="card-title fw-bold mb-0">Invoices ({clientInvoices.length})</h3>
        {activeRecurring.length > 0 && (
          <span className="badge badge-light-primary d-inline-flex align-items-center gap-1" title="Invoiced on a schedule — we issue it, the customer pays however they like">
            <i className="bi bi-arrow-repeat" />
            {activeRecurring.length} active recurring
          </span>
        )}
      </div>
      <div className="card-body py-3">
        {activeRecurring.length > 0 && (
          <div className="mb-4 p-3 rounded" style={{ background: 'rgba(59, 130, 246, 0.06)', border: '1px solid rgba(59, 130, 246, 0.25)' }}>
            <div className="d-flex align-items-center gap-2 mb-2 text-primary fw-semibold">
              <i className="bi bi-arrow-repeat" />
              Invoiced on a schedule
              <span className="text-muted fs-8 fw-normal">· we issue it, they pay however they like</span>
            </div>
            {activeRecurring.map((r) => (
              <div key={r.id} className="d-flex flex-wrap align-items-center gap-2 fs-7 text-muted py-1">
                <span className="fw-semibold text-gray-800">{freqLabel(r.frequency)}</span>
                <span>·</span>
                <span>{formatCurrency(templateTotal(r))}{r.templateType === 'bill' ? ' bill' : ''}</span>
                {r.nextDate && (<><span>·</span><span>next {formatDate(r.nextDate)}</span></>)}
                {r.endDate && (<><span>·</span><span>until {formatDate(r.endDate)}</span></>)}
                {r.reference && (<><span>·</span><span className="fst-italic">{r.reference}</span></>)}
              </div>
            ))}
          </div>
        )}
        {clientInvoices.length === 0 ? (
          <div className="text-center py-10">
            <i className="bi bi-receipt fs-1 text-muted"></i>
            <div className="text-muted mt-3">No invoices found for this customer</div>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4">
              <thead>
                <tr className="fw-bold text-muted">
                  <th className="min-w-120px">Invoice #</th>
                  <th className="min-w-100px">Status</th>
                  <th className="min-w-100px">Date</th>
                  <th className="min-w-100px">Due Date</th>
                  <th className="min-w-100px text-end">Total</th>
                  <th className="min-w-100px text-end">Amount Due</th>
                </tr>
              </thead>
              <tbody>
                {clientInvoices.map((invoice) => {
                  const statusConfig = INVOICE_STATUS_CONFIG[invoice.status];
                  const isOverdue = invoice.status === 'APPROVED' && isDateOverdue(invoice.dueDate);
                  
                  return (
                    <tr key={invoice.id}>
                      <td>
                        <span
                          className="text-primary fw-bold cursor-pointer text-hover-dark"
                          role="button"
                          onClick={() => setSelectedInvoice(invoice)}
                        >
                          {invoice.invoiceNumber}
                        </span>
                        {invoice.reference && (
                          <span className="text-muted fs-8 d-block">{invoice.reference}</span>
                        )}
                      </td>
                      <td>
                        <span className={`badge ${statusConfig?.badgeClass || 'badge-light-secondary'}`}>
                          {statusConfig?.label || invoice.status}
                        </span>
                        {isOverdue && (
                          <span className="badge badge-light-danger ms-1">Overdue</span>
                        )}
                      </td>
                      <td className="text-muted fs-7">{formatDate(invoice.date)}</td>
                      <td className={`fs-7 ${isOverdue ? 'text-danger fw-semibold' : 'text-muted'}`}>
                        {formatDate(invoice.dueDate)}
                      </td>
                      <td className="text-end fw-semibold">{formatCurrency(invoice.total)}</td>
                      <td className="text-end">
                        <span className={invoice.amountDue > 0 ? 'text-warning fw-semibold' : 'text-muted'}>
                          {formatCurrency(invoice.amountDue)}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

  const renderBillsTab = () => (
    <div className="card">
      <div className="card-header d-flex align-items-center justify-content-between">
        <h3 className="card-title fw-bold mb-0">Bills ({clientBills.length})</h3>
        {activeRecurringBills.length > 0 && (
          <span className="badge badge-light-success d-inline-flex align-items-center gap-1" title="This supplier has active recurring bill templates">
            <i className="bi bi-arrow-repeat" />
            {activeRecurringBills.length} active recurring
          </span>
        )}
      </div>
      <div className="card-body py-3">
        {activeRecurringBills.length > 0 && (
          <div className="mb-4 p-3 rounded" style={{ background: 'rgba(59, 130, 246, 0.06)', border: '1px solid rgba(59, 130, 246, 0.25)' }}>
            <div className="d-flex align-items-center gap-2 mb-2 text-primary fw-semibold">
              <i className="bi bi-arrow-repeat" />
              Billed on a schedule
            </div>
            {activeRecurringBills.map((r) => (
              <div key={r.id} className="d-flex flex-wrap align-items-center gap-2 fs-7 text-muted py-1">
                <span className="fw-semibold text-gray-800">{freqLabel(r.frequency)}</span>
                <span>·</span>
                <span>{formatCurrency(templateTotal(r))}</span>
                {r.nextDate && (<><span>·</span><span>next {formatDate(r.nextDate)}</span></>)}
                {r.endDate && (<><span>·</span><span>until {formatDate(r.endDate)}</span></>)}
                {r.reference && (<><span>·</span><span className="fst-italic">{r.reference}</span></>)}
              </div>
            ))}
          </div>
        )}
        {clientBills.length === 0 ? (
          <div className="text-center py-10">
            <i className="bi bi-receipt fs-1 text-muted"></i>
            <div className="text-muted mt-3">No bills found for this supplier</div>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4">
              <thead>
                <tr className="fw-bold text-muted">
                  <th className="min-w-120px">Bill #</th>
                  <th className="min-w-100px">Status</th>
                  <th className="min-w-100px">Date</th>
                  <th className="min-w-100px">Due Date</th>
                  <th className="min-w-100px text-end">Total</th>
                  <th className="min-w-100px text-end">Amount Due</th>
                </tr>
              </thead>
              <tbody>
                {clientBills.map((bill) => {
                  const statusConfig = BILL_STATUS_CONFIG[bill.status];
                  const isOverdue = (bill.status === 'APPROVED' || bill.status === 'SENT') && bill.amountDue > 0 && isDateOverdue(bill.dueDate);

                  return (
                    <tr key={bill.id}>
                      <td>
                        <span
                          className="text-primary fw-bold cursor-pointer text-hover-dark"
                          role="button"
                          onClick={() => setSelectedBill(bill)}
                        >
                          {bill.billNumber}
                        </span>
                        {bill.reference && (
                          <span className="text-muted fs-8 d-block">{bill.reference}</span>
                        )}
                      </td>
                      <td>
                        <span className={`badge ${statusConfig?.badgeClass || 'badge-light-secondary'}`}>
                          {statusConfig?.label || bill.status}
                        </span>
                        {isOverdue && (
                          <span className="badge badge-light-danger ms-1">Overdue</span>
                        )}
                      </td>
                      <td className="text-muted fs-7">{formatDate(bill.date)}</td>
                      <td className={`fs-7 ${isOverdue ? 'text-danger fw-semibold' : 'text-muted'}`}>
                        {formatDate(bill.dueDate)}
                      </td>
                      <td className="text-end fw-semibold">{formatCurrency(bill.total)}</td>
                      <td className="text-end">
                        <span className={bill.amountDue > 0 ? 'text-warning fw-semibold' : 'text-muted'}>
                          {formatCurrency(bill.amountDue)}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

  // ── Recurring Stripe arrangements for this contact ────────────────────────
  const renderRecurringTab = () => (
    <div className="card">
      <div className="card-header d-flex align-items-center justify-content-between flex-wrap gap-2">
        <h3 className="card-title fw-bold mb-0">Recurring</h3>
        <div className="d-flex gap-2">
          {activeRecurring.length > 0 && (
            <button className="btn btn-sm btn-light-primary" onClick={() => navigate('/accounting?view=recurring')}>
              <i className="bi bi-arrow-repeat me-1" />Invoice templates
            </button>
          )}
          <button className="btn btn-sm btn-light-primary" onClick={() => navigate('/recurring-payments')}>
            <i className="bi bi-box-arrow-up-right me-1" />All arrangements
          </button>
        </div>
      </div>
      <div className="card-body py-3">
        {doubleBillingRisk && (
          <div className="alert alert-warning py-2 px-3 fs-8 mb-4 d-flex align-items-center gap-2">
            <i className="bi bi-exclamation-triangle" />
            This contact is BOTH charged automatically and invoiced on the same cadence — check you're not billing twice.
          </div>
        )}

        {/* ── Charged automatically (Stripe, card on file) ──────────────────
            Money IN only, so it's hidden for suppliers — but if one somehow has
            an arrangement, still label it rather than render headless cards. */}
        {(!isSupplier || clientSubscriptions.length > 0) && (
          <>
            <div className="d-flex align-items-center gap-2 mb-1">
              <i className="bi bi-credit-card text-success" />
              <span className="fw-bold text-gray-800 fs-6">Charged automatically</span>
            </div>
            <div className="text-muted fs-8 mb-3">
              Card on file — Stripe collects the payment. Each one creates a PAID invoice.
            </div>
            {clientSubscriptions.length === 0 && (
              <div className="text-muted fs-8 fst-italic mb-4">
                No Stripe arrangement. Create one on the Recurring Payments page and it links here automatically.
              </div>
            )}
          </>
        )}

        {clientSubscriptions.length > 0 && (
          <div className="d-flex flex-column gap-4 mb-5">
            {clientSubscriptions.map((sub) => {
              const term = termInfo(sub);
              const end = endInfo(sub);
              const rows = paymentsFor(sub.id);
              // Net of refunds — gross overstates what was actually kept.
              const received = rows.reduce((s, p) => s + (p.amount || 0) - (p.refundedAmount || 0), 0);
              const pct = term.fixedTerm && term.total
                ? Math.min(100, Math.round((term.paid / term.total) * 100))
                : null;
              return (
                <div key={sub.id} className="border border-gray-300 rounded p-4">
                  <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
                    <span className="fw-bold text-dark fs-4">
                      {formatCurrency(sub.currentAmount)}
                      <span className="text-muted fs-8 fw-semibold">/{sub.interval}</span>
                    </span>
                    <span className={`badge ${sub.status === 'active' ? 'badge-light-success' : 'badge-light-warning'}`}>
                      {sub.status.replace(/_/g, ' ')}
                    </span>
                    {sub.testMode && <span className="badge badge-light-danger fs-8">TEST</span>}
                    <span className="text-muted fs-7">{sub.planName || 'Monthly support'}</span>
                    <span className="ms-auto text-muted fs-8">
                      {end.ended || end.reason === 'cancelled'
                        ? 'No further charges'
                        : `Next charge ${formatDate(sub.currentPeriodEnd)}`}
                    </span>
                  </div>

                  {/* Say WHY it's ending — a bare end date can't tell a walk-away
                      from an agreed term finishing. */}
                  {end.label && (
                    <div className={`d-flex align-items-center gap-2 mb-3 text-${end.tone} fs-8`}>
                      <i className={`bi bi-${end.reason === 'cancelled' ? 'person-dash' : 'check2-circle'}`} />
                      {end.label}
                    </div>
                  )}

                  <div className="d-flex flex-wrap gap-5 mb-3">
                    <div>
                      <div className="text-muted fs-8">Term</div>
                      <div className="fw-semibold text-gray-800 fs-7">{term.label}</div>
                      {pct !== null && (
                        <div className="progress mt-1" style={{ height: 4, minWidth: 160 }}>
                          <div className="progress-bar bg-primary" style={{ width: `${pct}%` }} />
                        </div>
                      )}
                    </div>
                    <div>
                      <div className="text-muted fs-8">Received to date</div>
                      <div className="fw-semibold text-success fs-7">{formatCurrency(received)}</div>
                    </div>
                    <div>
                      <div className="text-muted fs-8">Payments</div>
                      <div className="fw-semibold text-gray-800 fs-7">{rows.length}</div>
                    </div>
                  </div>

                  {rows.length > 0 && (
                    <div className="table-responsive">
                      <table className="table table-row-dashed table-row-gray-200 align-middle gs-0 gy-2 mb-0">
                        <thead>
                          <tr className="fw-bold text-muted fs-8 text-uppercase">
                            <th>Paid</th>
                            <th>Period</th>
                            <th className="text-end">Amount</th>
                            <th>Invoice</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((p) => (
                            <tr key={p.id}>
                              <td className="text-muted fs-8">{formatDate(p.paidAt)}</td>
                              <td className="text-muted fs-8">
                                {p.periodStart || p.periodEnd
                                  ? `${formatDate(p.periodStart)} – ${formatDate(p.periodEnd)}`
                                  : '—'}
                              </td>
                              <td className="text-end fw-semibold fs-8">{formatCurrency(p.amount)}</td>
                              <td className="fs-8">
                                {p.nativeInvoiceNumber ? (
                                  <span className="d-inline-flex align-items-center gap-1 flex-wrap">
                                    <button
                                      type="button"
                                      className="badge badge-light-success border-0"
                                      style={{ cursor: 'pointer' }}
                                      title="Open this invoice"
                                      onClick={() => openInvoiceById(p.nativeInvoiceId!)}
                                    >
                                      {p.nativeInvoiceNumber} · paid
                                    </button>
                                    {p.arrangementPhase === 2 && (
                                      <span className="badge badge-light-info" title="Stepped-down rate">phase 2</span>
                                    )}
                                    {p.refundedAmount > 0 && (
                                      <span className="badge badge-light-danger" title="Refunded to the customer">
                                        refunded {formatCurrency(p.refundedAmount)}
                                      </span>
                                    )}
                                  </span>
                                ) : p.invoiceError ? (
                                  <span className="badge badge-light-danger" title={p.invoiceError}>needs attention</span>
                                ) : <span className="badge badge-light-warning">not invoiced</span>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ── Invoiced on a schedule (templates — customer pays however) ────
            Split by direction: an INVOICE template is money in, a BILL template
            is money OUT (they invoice us). Calling both "invoices we issue"
            inverted the money direction on every supplier. */}
        {activeRecurringInvoices.length > 0 && (
          <>
            <div className="d-flex align-items-center gap-2 mb-1">
              <i className="bi bi-arrow-repeat text-primary" />
              <span className="fw-bold text-gray-800 fs-6">Invoiced on a schedule</span>
            </div>
            <div className="text-muted fs-8 mb-3">
              We issue an invoice on this cadence; the customer pays it however they like. No card is charged.
            </div>
            <div className="d-flex flex-column gap-3">
              {activeRecurringInvoices.map((r) => (
                <div key={r.id} className="border border-gray-300 rounded p-4">
                  <div className="d-flex flex-wrap align-items-center gap-3 mb-2">
                    <span className="fw-bold text-dark fs-4">
                      {formatCurrency(templateTotal(r))}
                      <span className="text-muted fs-8 fw-semibold">/{freqLabel(r.frequency).toLowerCase()}</span>
                    </span>
                    <span className="badge badge-light-primary">{r.templateType === 'bill' ? 'Bill' : 'Invoice'} template</span>
                    <span className="ms-auto text-muted fs-8">
                      {r.nextDate ? `Next issued ${formatDate(r.nextDate)}` : 'No next date'}
                    </span>
                  </div>
                  <div className="d-flex flex-wrap gap-5">
                    {r.endDate && (
                      <div>
                        <div className="text-muted fs-8">Until</div>
                        <div className="fw-semibold text-gray-800 fs-7">{formatDate(r.endDate)}</div>
                      </div>
                    )}
                    {r.reference && (
                      <div>
                        <div className="text-muted fs-8">Reference</div>
                        <div className="fw-semibold text-gray-800 fs-7">{r.reference}</div>
                      </div>
                    )}
                    <div>
                      <div className="text-muted fs-8">Auto email draft</div>
                      <div className="fw-semibold text-gray-800 fs-7">
                        {r.autoEmailDraft === false ? 'Off' : 'On (review before sending)'}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Money OUT — a supplier invoices US on this cadence. */}
        {activeRecurringBills.length > 0 && (
          <>
            <div className="d-flex align-items-center gap-2 mb-1 mt-4">
              <i className="bi bi-arrow-repeat text-warning" />
              <span className="fw-bold text-gray-800 fs-6">Billed to us on a schedule</span>
            </div>
            <div className="text-muted fs-8 mb-3">
              A bill is raised on this cadence for us to pay. Money out, not in.
            </div>
            <div className="d-flex flex-column gap-3">
              {activeRecurringBills.map((r) => (
                <div key={r.id} className="border border-gray-300 rounded p-4">
                  <div className="d-flex flex-wrap align-items-center gap-3">
                    <span className="fw-bold text-dark fs-4">
                      {formatCurrency(templateTotal(r))}
                      <span className="text-muted fs-8 fw-semibold">/{freqLabel(r.frequency).toLowerCase()}</span>
                    </span>
                    <span className="badge badge-light-warning">Bill template</span>
                    {r.reference && <span className="text-muted fs-8">{r.reference}</span>}
                    <span className="ms-auto text-muted fs-8">
                      {r.nextDate ? `Next raised ${formatDate(r.nextDate)}` : 'No next date'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );

  // ── Command-center spine: state line + the single next action ─────────────
  const renderRelationshipHeader = () => (
    <div className="card mb-5">
      <div className="card-body py-4">
        <div className="d-flex align-items-center flex-wrap gap-3 mb-3">
          <span className={`badge badge-light-${signal.tone} fs-7 fw-bold`}>{signal.label}</span>
          <span className="text-gray-700 fs-7">{stateBits}</span>
          <button
            className="btn btn-sm btn-light-primary ms-auto"
            onClick={() => askAndrei(`Give me the full picture on ${client.company || client.name}: our history, where the relationship stands now, and what I should do next.`)}
          >
            <i className="bi bi-stars me-2"></i>Ask Andrei
          </button>
        </div>
        <div className={`d-flex align-items-center gap-3 p-3 rounded bg-light-${nextAction.tone}`}>
          <i className={`bi bi-arrow-right-circle-fill text-${nextAction.tone} fs-4`}></i>
          <span className="fw-semibold text-gray-800 flex-grow-1">{nextAction.label}</span>
          {nextAction.cta && nextAction.run && (
            <button
              className={`btn btn-sm ${nextAction.tone === 'secondary' ? 'btn-light' : `btn-light-${nextAction.tone}`}`}
              onClick={nextAction.run}
            >
              {nextAction.cta}
            </button>
          )}
        </div>
      </div>
    </div>
  );

  // ── Unified relationship timeline — the story (emails + invoices + jobs +
  //    events) in one searchable stream. ──────────────────────────────────────
  const renderTimeline = () => {
    const q = tlSearch.trim().toLowerCase();
    const filtered = q ? timeline.filter(it => `${it.title} ${it.sub}`.toLowerCase().includes(q)) : timeline;
    const shown = q ? filtered : filtered.slice(0, tlVisible);
    return (
      <div className="col-12">
        <div className="card">
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap gap-2">
            <h3 className="card-title fw-bold mb-0">Relationship Timeline</h3>
            {correspondenceError && <div className="alert alert-warning mb-0 w-100" role="alert">Some correspondence could not load: {correspondenceError}</div>}
            <div className="position-relative">
              <i className="bi bi-search position-absolute top-50 translate-middle-y text-muted" style={{ left: 12, fontSize: 12 }}></i>
              <input
                type="text"
                className="form-control form-control-sm ps-8"
                style={{ minWidth: 240 }}
                placeholder="Search emails, invoices, jobs…"
                value={tlSearch}
                onChange={(e) => setTlSearch(e.target.value)}
              />
            </div>
          </div>
          <div className="card-body py-4">
            {timeline.length === 0 ? (
              <div className="text-center py-8 text-muted">No activity recorded yet.</div>
            ) : shown.length === 0 ? (
              <div className="text-center py-8 text-muted">Nothing matches “{tlSearch}”.</div>
            ) : (
              <div className="d-flex flex-column">
                {shown.map((it, i) => (
                  <div
                    key={i}
                    className={`d-flex align-items-start gap-3 ${it.run ? 'cursor-pointer' : ''}`}
                    onClick={it.run}
                    role={it.run ? 'button' : undefined}
                  >
                    <span className="symbol symbol-35px flex-shrink-0">
                      <span className={`symbol-label bg-light-${it.tone}`}>
                        <i className={`bi bi-${it.icon} text-${it.tone} fs-6`}></i>
                      </span>
                    </span>
                    <div className="flex-grow-1 border-bottom pb-3 mb-3">
                      <div className="d-flex justify-content-between align-items-start gap-2">
                        <span className="fw-semibold text-gray-800">{it.title}</span>
                        <span className="text-muted fs-8 flex-shrink-0">{formatDate(new Date(it.ms))}</span>
                      </div>
                      {it.sub && <div className="text-muted fs-8 mt-1">{it.sub}</div>}
                    </div>
                  </div>
                ))}
                {!q && filtered.length > tlVisible && (
                  <button
                    type="button"
                    className="btn btn-sm btn-light-primary align-self-center mt-3"
                    onClick={() => setTlVisible(v => v + TIMELINE_PAGE)}
                  >
                    Show {Math.min(TIMELINE_PAGE, filtered.length - tlVisible)} more
                    <span className="opacity-75 ms-2">· {filtered.length - tlVisible} earlier</span>
                  </button>
                )}
                {q && (
                  <div className="text-muted fs-8 text-center pt-1">
                    {filtered.length} match{filtered.length === 1 ? '' : 'es'}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="contact-detail-panel">
      {/* Header */}
      <div className="card mb-5">
        <div className="card-body py-4">
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center">
              <button 
                className="btn btn-icon btn-light-primary me-4"
                onClick={onClose}
              >
                <i className="bi bi-arrow-left fs-4"></i>
              </button>
              <div className="symbol symbol-60px me-4">
                <span className="symbol-label bg-light-primary text-primary fw-bold fs-2">
                  {(client.company || client.name || '?').charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <h2 className="mb-0 fw-bold">{client.company || client.name}</h2>
                <div className="d-flex align-items-center gap-3 mt-1">
                  {client.company && client.name && (
                    <span className="text-muted">{client.name}</span>
                  )}
                  {getContactTypeBadge(client.contactType)}
                </div>
              </div>
            </div>
            <div className="d-flex gap-2">
              {!isSupplier && (activeRecurringInvoices.length > 0 || clientSubscriptions.length > 0 || clientJobs.some(job => (job as any).support)) && (
                <button type="button" className="btn btn-light-primary" onClick={() => navigate(`/monthly-support?id=${encodeURIComponent(client.id || '')}`)}>
                  <i className="bi bi-check2-square me-2"></i>Monthly support
                </button>
              )}
              <button 
                className="btn btn-light-primary"
                onClick={() => setShowEditModal(true)}
              >
                <i className="bi bi-pencil me-2"></i>
                Edit
              </button>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="d-flex flex-wrap gap-5 mt-5 pt-4 border-top">
            <div>
              <span className="text-muted fs-8">Jobs</span>
              <div className="d-flex align-items-center gap-2">
                <span className="fs-4 fw-bold text-gray-800">{jobCounts.total}</span>
                {jobCounts.active > 0 && (
                  <span className="badge badge-light-warning">{jobCounts.active} active</span>
                )}
              </div>
            </div>
            <div className="border-start ps-5">
              <span className="text-muted fs-8">Revenue</span>
              <div className="fs-4 fw-bold text-success">{formatCurrency(revenue.total)}</div>
            </div>
            <div className="border-start ps-5">
              <span className="text-muted fs-8">Outstanding</span>
              <div className={`fs-4 fw-bold ${revenue.outstanding > 0 ? 'text-warning' : 'text-muted'}`}>
                {formatCurrency(revenue.outstanding)}
              </div>
            </div>
            <div className="border-start ps-5">
              <span className="text-muted fs-8">{isSupplier ? 'Bills' : 'Invoices'}</span>
              <div className="fs-4 fw-bold text-gray-800">{isSupplier ? clientBills.length : clientInvoices.length}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Command-center spine — the relationship's current state + next move */}
      {renderRelationshipHeader()}

      {/* Tabs */}
      <ul className="nav nav-tabs nav-line-tabs nav-line-tabs-2x mb-5 fs-6">
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            <i className="bi bi-info-circle me-2"></i>
            Overview
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'jobs' ? 'active' : ''}`}
            onClick={() => setActiveTab('jobs')}
          >
            <i className="bi bi-briefcase me-2"></i>
            Jobs
            {jobCounts.total > 0 && (
              <span className="badge badge-light-primary ms-2">{jobCounts.total}</span>
            )}
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'invoices' ? 'active' : ''}`}
            onClick={() => setActiveTab('invoices')}
          >
            <i className="bi bi-receipt me-2"></i>
            {isSupplier ? 'Bills' : 'Invoices'}
            {(isSupplier ? clientBills.length : clientInvoices.length) > 0 && (
              <span className="badge badge-light-primary ms-2">{isSupplier ? clientBills.length : clientInvoices.length}</span>
            )}
            {(isSupplier ? activeRecurringBills.length : activeRecurring.length) > 0 && (
              <i
                className="bi bi-arrow-repeat text-primary ms-2"
                title={
                  isSupplier
                    ? `${activeRecurringBills.length} active recurring bill${activeRecurringBills.length === 1 ? '' : 's'}`
                    : `${activeRecurring.length} active recurring invoice${activeRecurring.length === 1 ? '' : 's'}`
                }
              />
            )}
          </button>
        </li>
        {/* Recurring — negotiated monthly support arrangements paid via Stripe.
            Only shown when this contact actually has one. */}
        {/* ONE Recurring tab covering BOTH mechanisms, each with its own mark:
            a card-on-file arrangement (green, money arrives by itself) vs a
            scheduled invoice template (blue, we issue, they pay however). */}
        {(clientSubscriptions.length > 0 || activeRecurring.length > 0) && (
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'recurring' ? 'active' : ''}`}
              onClick={() => setActiveTab('recurring')}
            >
              <i className="bi bi-credit-card me-2"></i>
              Recurring
              {clientSubscriptions.length > 0 && (
                <span
                  className="badge badge-light-success ms-2"
                  title="Stripe arrangements — charged automatically"
                >
                  {clientSubscriptions.length}
                </span>
              )}
              {activeRecurring.length > 0 && (
                <i
                  className="bi bi-arrow-repeat text-primary ms-2"
                  title={`${activeRecurring.length} scheduled invoice template${activeRecurring.length === 1 ? '' : 's'}`}
                />
              )}
              {failingSubscriptions.length > 0 && (
                <i className="bi bi-exclamation-triangle-fill text-danger ms-2" title="A card has been declined" />
              )}
            </button>
          </li>
        )}
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'conversations' ? 'active' : ''}`}
            onClick={() => setActiveTab('conversations')}
          >
            <i className="bi bi-chat-square-text me-2"></i>
            Conversations
          </button>
        </li>
        {/* Google Ads tab — shows a "bind" CTA when the customer has no
            googleAdsCustomerId, otherwise renders KPIs / campaigns /
            true-ROAS for the bound account. */}
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'ads' ? 'active' : ''}`}
            onClick={() => setActiveTab('ads')}
          >
            <i className="bi bi-google me-2"></i>
            Google Ads
            {client.googleAdsCustomerId && (
              <span className="badge badge-light-success ms-2" title="Bound to a Google Ads account">●</span>
            )}
          </button>
        </li>
        {/* Hosting / SSH — encrypted, server-only per-contact access store. */}
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'hosting' ? 'active' : ''}`}
            onClick={() => setActiveTab('hosting')}
          >
            <i className="bi bi-hdd-network me-2"></i>
            Hosting / SSH
          </button>
        </li>
      </ul>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'overview' && renderOverviewTab()}
        {activeTab === 'jobs' && renderJobsTab()}
        {activeTab === 'invoices' && (isSupplier ? renderBillsTab() : renderInvoicesTab())}
        {activeTab === 'recurring' && renderRecurringTab()}
        {activeTab === 'conversations' && (
          <ClientConversationsTab
            customerId={client.id || ''}
            customerName={client.name || client.company || ''}
          />
        )}
        {activeTab === 'ads' && <ClientAdsTab client={client} />}
        {activeTab === 'hosting' && (
          <ClientHostingTab customerId={client.id || ''} customerName={client.company || client.name || ''} />
        )}
      </div>

      {/* Edit Client Modal */}
      <NewCustomerModal
        show={showEditModal}
        onClose={() => setShowEditModal(false)}
        customer={client}
        onDeleted={() => {
          setShowEditModal(false);
          onClose();
        }}
        deleteSummary={{
          jobs: clientJobs.length,
          invoices: clientInvoices.length,
          events: clientEvents.length,
        }}
      />

      {/* Job Edit Modal */}
      {selectedJob && (
        <JobEditModal
          show={showJobEditModal}
          job={selectedJob}
          onHide={() => {
            setShowJobEditModal(false);
            setSelectedJob(null);
          }}
        />
      )}

      {/* Job Workspace Modal */}
      {selectedJob && (
        <JobWorkspaceModal
          show={showTaskModal}
          job={selectedJob}
          onHide={() => {
            setShowTaskModal(false);
            setSelectedJob(null);
          }}
        />
      )}

      {/* Invoice View Modal */}
      {selectedInvoice && (
        <InvoiceProvider>
          <InvoiceViewModal
            show={!!selectedInvoice}
            invoice={selectedInvoice}
            onHide={() => setSelectedInvoice(null)}
          />
        </InvoiceProvider>
      )}

      {/* Bill View Modal (suppliers) */}
      {selectedBill && (
        <BillsProvider>
          <BillViewModal
            show={!!selectedBill}
            bill={selectedBill}
            onHide={() => setSelectedBill(null)}
          />
        </BillsProvider>
      )}
    </div>
  );
}

export default ClientDetailPanel;
